import {useCallback, useContext, useEffect, useState} from 'react';

import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import DownloadIcon from '@mui/icons-material/Download';
import {CircularProgress, Grid} from '@mui/material';

import {ActionButton} from '../DetailedCardView/ActionButton';

import {RejectMatch} from './RejectMatch';
import {EmpionButton} from '../../../../../../components/EmpionButton/EmpionButton';
import {CompanyContext} from '../../../../../../contexts/CompanyContext/CompanyContext';
import {SnackBarContext} from '../../../../../../contexts/SnackBarContext/SnackBarContext';
import {useApiWrite} from '../../../../../../hooks/useApiWrite';
import {useDownloadPdfProfile} from '../../../../../../hooks/useDownloadPdfProfile';
import {useMatchesSorting} from '../../../../../../hooks/useMatchesSorting';
import {useTrackingEvents} from '../../../../../../hooks/useTrackingEvents';
import {Patch} from '../../../../../../lib/api/PATCH/Patch';
import {PatchRoutes} from '../../../../../../lib/api/PATCH/PatchRoutes';
import {MATCH_DETAILS_QUERY_KEY} from '../../../../../../pages/company/recruiting/MatchDetailsPage';
import {MatchStatus} from '../../../../../../types/MatchStatus/MatchStatus';
import {MatchData} from '../../../../../../types/shared-recruiting-types';
import {TrackingEvents} from '../../../../../../types/TrackingEvents';

interface Props {
  match: MatchData;
}

export const Actions = ({match}: Props) => {
  const {t: tCardDetails} = useTranslation('translation', {keyPrefix: 'companyApplicants.applicantsCard.cardDetails'});
  const {t} = useTranslation();
  const {t: tr} = useTranslation('translation', {keyPrefix: 'companyApplicants.applicantsCard.menuItems'});
  const {company} = useContext(CompanyContext);
  const companyId = company.data?.resourceId || '';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const {dispatchMessage} = useContext(SnackBarContext);
  const queryClient = useQueryClient();

  const {Version, Action, Category} = TrackingEvents;
  const {eventAttributes} = useTrackingEvents({
    version: Version.v1,
    action: Action.UserDownloadedPdf,
    category: Category.ApplicantPdf,
    payload: {},
  });

  const {refreshData: refreshMatchesIndex} = useMatchesSorting(companyId);

  const {downloadPdf, isLoading: isPdfDownloading} = useDownloadPdfProfile({
    companyId: company?.data?.resourceId || '',
    matchId: match.resourceId,
    // eslint-disable-next-line max-len
    fileName: `${match.applicant?.personalInformation.firstName}_${match.applicant?.personalInformation.lastName}_profile.pdf`,
    onError: () => dispatchMessage(tCardDetails('noPdfData'), 'error'),
  });

  const handlePdfExport = useCallback(async () => {
    await downloadPdf();
  }, [downloadPdf]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const closeStatusMenu = () => {
    setAnchorEl(null);
  };

  const onSuccess = () => {
    refreshMatchesIndex();
    queryClient.invalidateQueries({queryKey: [MATCH_DETAILS_QUERY_KEY]});
    queryClient.invalidateQueries({queryKey: [`match-activity-${match.resourceId}`]});
    closeStatusMenu();
  };

  const {makeRequest: makeRequestContact, error: isContactError} = useApiWrite<Patch.ContactMatch>({
    method: 'patch',
    path: PatchRoutes[Patch.ContactMatch](companyId, match.resourceId),
    onSuccess,
  });

  const {makeRequest: makeRequestInterview, error: isInterviewError} = useApiWrite<Patch.InterviewMatch>({
    method: 'patch',
    path: PatchRoutes[Patch.InterviewMatch](companyId, match.resourceId),
    onSuccess,
  });

  const {makeRequest: makeRequestHire, error: isHireError} = useApiWrite<Patch.HireMatch>({
    method: 'patch',
    path: PatchRoutes[Patch.HireMatch](companyId, match.resourceId),
    onSuccess,
  });

  const {makeRequest: makeRequestAccept, error: isAcceptError} = useApiWrite<Patch.ReAcceptMatchByCompany>({
    method: 'patch',
    path: PatchRoutes[Patch.ReAcceptMatchByCompany](companyId, match.resourceId),
    onSuccess,
  });

  const {makeRequest: makeRequestOnHold, error: isOnHoldError} = useApiWrite<Patch.OnHoldMatch>({
    method: 'patch',
    path: PatchRoutes[Patch.OnHoldMatch](companyId, match.resourceId),
    onSuccess,
  });

  useEffect(() => {
    if (isContactError || isInterviewError || isHireError || isAcceptError || isOnHoldError) {
      dispatchMessage(t('companyApplicants.snackbars.error'), 'error');
    }
  }, [isContactError, isInterviewError, isHireError, isAcceptError, isOnHoldError, dispatchMessage, t]);

  const handleRejectSuccess = () => {
    dispatchMessage(t('shared.tags.statusUpdate'), 'success');
    setModalOpen(false);
    onSuccess();
  };
  const {makeRequest, isLoading, error} = useApiWrite<Patch.RejectMatchByCompany>({
    method: 'patch',
    path: PatchRoutes[Patch.RejectMatchByCompany](companyId, match.resourceId),
    onSuccess: handleRejectSuccess,
  });

  const menuItemsActionButton = [
    {
      key: 'accepedAt',
      label: tr('new').toUpperCase(),
      onClick: async () => {
        makeRequestAccept();
      },
      variant: 'body1',
      disabled: match.status === MatchStatus.Accepted,
    },
    {
      key: 'contactedAt',
      label: tr('contacted').toUpperCase(),
      onClick: async () => {
        makeRequestContact();
      },
      variant: 'body1',
      disabled: match.status === MatchStatus.Contacted,
    },
    {
      key: 'interviewAt',
      label: tr('interview').toUpperCase(),
      onClick: async () => {
        makeRequestInterview();
      },
      variant: 'body1',
      disabled: match.status === MatchStatus.Interview,
    },
    {
      key: 'hiredAt',
      label: tr('hired').toUpperCase(),
      onClick: async () => {
        makeRequestHire();
      },
      variant: 'body1',
      disabled: match.status === MatchStatus.Hired,
    },
    {
      key: 'onHoldAt',
      label: tr('onHold').toUpperCase(),
      onClick: async () => {
        makeRequestOnHold();
      },
      variant: 'body1',
      disabled: match.status === MatchStatus.OnHold,
    },
    {
      key: 'rejectedAt',
      label: tr('rejected').toUpperCase(),
      onClick: () => {
        setModalOpen(true);
      },
      variant: 'body1',
      disabled: match.status === MatchStatus.Rejected,
    },
  ];

  return (
    <>
      <Grid container alignItems='center' justifyContent='space-between'>
        <Grid item display={'flex'} alignItems='center'>
          <EmpionButton
            {...eventAttributes}
            onClick={handlePdfExport}
            variant='cta'
            sx={{
              minHeight: '32px',
              display: 'flex',
              alignItems: 'center',
            }}
            startIcon={
              isPdfDownloading ? (
                <CircularProgress size={16} sx={{color: 'white'}} />
              ) : (
                <DownloadIcon />
              )
            }
            disabled={isPdfDownloading}
          >
            {tCardDetails('exportProfile')}
          </EmpionButton>
        </Grid>
        <Grid item>
          <ActionButton
            status={match.status}
            menuItems={menuItemsActionButton}
            handleClick={handleClick}
            handleClose={closeStatusMenu}
            anchorEl={anchorEl}
            forceStatusLabel
            splitTrigger
            pulseAttention
          />
        </Grid>
      </Grid>
      <RejectMatch
        isModalOpen={modalOpen}
        setModalOpen={setModalOpen}
        onReject={(body) => makeRequest(body)}
        isLoading={isLoading}
        error={error}
      />
    </>
  );
};
